import React, { useEffect, useState, useRef, memo, Component } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Hero } from '../components/Hero';
import { Magnetic } from '../components/ui/Magnetic';
import { FloatingElements } from '../components/FloatingElements';
import { Sidebar } from '../components/Sidebar';
import { MasonryGrid } from '../components/MasonryGrid';
import { BurgerMenu } from '../components/BurgerMenu';
import { ThreeBackground } from '../components/ThreeBackground';
import { ContactModal } from '../components/ContactModal';
import { WorksLoading } from '../components/WorksLoading';
import { MobileFilterDropdown } from '../components/MobileFilterDropdown';
import { Mail, Pencil, Code2, Bitcoin } from 'lucide-react';
import { useTheme } from '../App';
interface HomePageProps {
  projects: any[]; // ProjectListItem[] with only lightweight fields
  onProjectClick: (project: any) => void;
  onGoToAdmin: () => void;
  onGoToProjects?: () => void;
  isLoadingProjects?: boolean;
}
// Cryptic phrases that change on each load
const crypticPhrases = ['REALITY IS MALLEABLE.', 'THE VOID WHISPERS.', 'TIME BENDS HERE.', 'PATTERNS EMERGE.', 'CHAOS BREEDS ORDER.', 'DIGITAL DREAMS MANIFEST.', 'THE SIGNAL PERSISTS.', 'ENTROPY REVERSED.', 'FRAGMENTS COALESCE.', 'THE GRID AWAKENS.', 'NOISE BECOMES SIGNAL.', 'BOUNDARIES DISSOLVE.', 'THE FUTURE ECHOES.', 'SYSTEMS CONVERGE.', 'MEANING CRYSTALLIZES.'];
// Companies/Clients with logos
const companies = [{
  name: 'Company 1',
  logo: 'https://cdn.magicpatterns.com/uploads/qT4hVnAj1X5iNMizJQnpqX/Group_1171276042.svg'
}, {
  name: 'Company 2',
  logo: 'https://cdn.magicpatterns.com/uploads/r7KuEjmCcpTvE7FrqRcR1a/Group_1171276043.svg'
}, {
  name: 'Company 3',
  logo: 'https://cdn.magicpatterns.com/uploads/3im9dUd7S3LU3Rwk2qSZE9/Group_1171276048.svg'
}, {
  name: 'Avastars',
  logo: 'https://cdn.magicpatterns.com/uploads/etYrwm28gNmwJN1qd9DFHb/Avastars_-_Logo_Horizontal_(2).svg'
}, {
  name: 'Company 5',
  logo: 'https://cdn.magicpatterns.com/uploads/87msmVzj1ZLEzzqxFgB5xe/Vector.svg'
}, {
  name: 'Sky',
  logo: 'https://cdn.magicpatterns.com/uploads/5xEykZnUbKyaKxhjGpySQz/sky.svg'
}, {
  name: 'Devcon',
  logo: 'https://cdn.magicpatterns.com/uploads/5indfsgxfU5hoq9jSqtbx4/devcon.svg'
}, {
  name: 'Sony',
  logo: 'https://cdn.magicpatterns.com/uploads/rTLqGqK6f4WAPVcin5gwYZ/Sony.svg'
}];
// X (Twitter) Logo Component
function XLogo({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} fill="currentColor" xmlns="http://www.w3.org/2000/svg" data-id="element-352">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" data-id="element-353" />
    </svg>;
}
// Typewriter effect component
function TypewriterText({
  text,
  delay = 0
}: {
  text: string;
  delay?: number;
}) {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, delay + (currentIndex === 0 ? 500 : 50)); // Initial delay + typing speed
      return () => clearTimeout(timeout);
    }
  }, [currentIndex, text, delay]);
  return <>{displayText}</>;
}
// Slot Machine Text Component (same as Hero)
function SlotMachineText({
  text,
  isTransitioning,
  speed = 'normal'
}: {
  text: string;
  isTransitioning: boolean;
  speed?: 'fast' | 'normal';
}) {
  const [displayText, setDisplayText] = useState(text);
  const prevTextRef = useRef(text);
  const scrambleChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ!@#$%^&*()';
  useEffect(() => {
    // If text hasn't changed, don't animate
    if (text === prevTextRef.current) {
      return;
    }
    prevTextRef.current = text;
    if (!isTransitioning) {
      setDisplayText(text);
      return;
    }
    // Animation speeds
    const scrambleSpeed = speed === 'fast' ? 10 : 15;
    const revealSpeed = speed === 'fast' ? 40 : 60;
    const scrambleCount = speed === 'fast' ? 2 : 3;
    let currentIndex = 0;
    const targetLength = text.length;
    const interval = setInterval(() => {
      if (currentIndex >= targetLength) {
        clearInterval(interval);
        setDisplayText(text);
        return;
      }
      let count = 0;
      const scrambleInterval = setInterval(() => {
        count++;
        if (count > scrambleCount) {
          clearInterval(scrambleInterval);
          setDisplayText(prev => {
            const chars = prev.split('');
            chars[currentIndex] = text[currentIndex];
            return chars.join('');
          });
          currentIndex++;
        } else {
          setDisplayText(prev => {
            const chars = prev.split('');
            if (text[currentIndex] === ' ') {
              chars[currentIndex] = ' ';
            } else {
              chars[currentIndex] = scrambleChars[Math.floor(Math.random() * scrambleChars.length)];
            }
            return chars.join('');
          });
        }
      }, scrambleSpeed);
    }, revealSpeed);
    return () => clearInterval(interval);
  }, [text, isTransitioning, speed]);
  return <>{displayText}</>;
}
// Contact Slot Machine Text Component
function ContactSlotMachineText() {
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const containerRef = useRef<HTMLHeadingElement>(null);
  const isInView = useInView(containerRef, {
    once: true,
    amount: 0.5
  });
  const [currentText, setCurrentText] = useState('話しましょう');
  useEffect(() => {
    if (isInView) {
      // Trigger transition after a brief delay
      const timer = setTimeout(() => {
        setCurrentText("LET'S TALK");
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [isInView]);
  return <h2 ref={containerRef} className={`text-[12vw] md:text-[8.5vw] font-black leading-none mb-12 tracking-tighter ${isDark ? 'text-white' : 'text-black'}`} data-id="element-354">
      <SlotMachineText text={currentText} isTransitioning={isInView} speed="normal" data-id="element-355" />
    </h2>;
}
export function HomePage({
  projects,
  onProjectClick,
  onGoToAdmin,
  onGoToProjects,
  isLoadingProjects = false
}: HomePageProps) {
  const {
    scrollYProgress
  } = useScroll();
  const [activeCategory, setActiveCategory] = useState('All');
  const [visibleCount, setVisibleCount] = useState(6);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const {
    theme
  } = useTheme();
  const [crypticPhrase] = useState(() => crypticPhrases[Math.floor(Math.random() * crypticPhrases.length)]);
  // Parallax for sections
  const yProjects = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const yAbout = useTransform(scrollYProgress, [0, 1], [0, -50]);
  // Fade out header text on scroll
  const headerOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);
  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  // Get all unique categories that have projects
  const allCategories = ['All', ...Array.from(new Set(projects.flatMap((p: any) => Array.isArray(p.category) ? p.category : [p.category]).filter(Boolean))).sort()];
  const filteredProjects = activeCategory === 'All' ? projects : projects.filter((p: any) => {
    // Handle both string and array categories
    if (Array.isArray(p.category)) {
      return p.category.some((cat: string) => cat.toLowerCase() === activeCategory.toLowerCase());
    }
    return p.category && p.category.toLowerCase() === activeCategory.toLowerCase();
  });
  const visibleProjects = filteredProjects.slice(0, visibleCount);
  const hasMore = visibleCount < filteredProjects.length;
  const handleLoadMore = () => {
    setVisibleCount(prev => Math.min(prev + 6, filteredProjects.length));
  };
  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    setVisibleCount(6);
  };
  const isDark = theme === 'dark';
  return <div className={`min-h-screen selection:bg-white selection:text-black overflow-x-hidden transition-colors duration-500 ${isDark ? 'bg-black text-white' : 'bg-white text-black'}`} data-id="element-356">
      {/* Three.js Background - Fixed across entire page */}
      <div className="fixed inset-0 z-0" data-id="element-357">
        <ThreeBackground data-id="element-358" />
      </div>

      <FloatingElements data-id="element-359" />
      <BurgerMenu onGoToAdmin={onGoToAdmin} onGoToProjects={onGoToProjects} currentPage="home" data-id="element-360" />

      {/* Header - Same as ProjectPage */}
      <nav className="fixed top-0 left-0 w-full z-[30] pointer-events-none backdrop-blur-sm" style={{
      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)'
    }} data-id="element-361">
        <div className="p-4 md:p-8 flex items-center" data-id="element-362">
          <div className="pointer-events-auto" data-id="element-363">
            <button onClick={scrollToTop} className={`text-2xl font-black tracking-tighter transition-opacity duration-300 hover:opacity-70 ${isDark ? 'text-white' : 'text-black'}`} data-id="element-364">
              Jav.
            </button>
          </div>
          <div className="flex-1" data-id="element-365" />
        </div>
      </nav>

      {/* Navigation - Logo on left, GM text aligned with hero H1 */}
      <nav className="fixed top-0 left-0 w-full z-[30] pointer-events-none backdrop-blur-sm" style={{
      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)'
    }} data-id="element-366">
        <div className="p-4 md:p-8 flex items-center" data-id="element-367">
          {/* Logo - left side */}
          <div className="pointer-events-auto" data-id="element-368">
            <button onClick={scrollToTop} className={`text-2xl font-black tracking-tighter transition-opacity duration-300 hover:opacity-70 ${isDark ? 'text-white' : 'text-black'}`} data-id="element-369">
              Jav.
            </button>
          </div>

          {/* Spacer to push GM text to align with hero content */}
          <div className="flex-1" data-id="element-370" />
        </div>

        {/* GM text - positioned to align exactly with H1 below, moved 18px down, centered on viewports < 1600px */}
        <motion.div className="hidden md:block absolute left-0 right-0 pointer-events-none" style={{
        top: 'calc(1rem + 18px)',
        opacity: headerOpacity
      }} data-id="element-371">
          <div className="px-4 md:px-12" data-id="element-372">
            <div className="max-w-7xl mx-auto" data-id="element-373">
              <p className="text-xs uppercase tracking-wider font-light max-[1599px]:text-center" style={{
              color: isDark ? '#999' : '#666',
              letterSpacing: '1.5px',
              position: 'relative',
              top: '8px'
            }} data-id="element-374">
                <TypewriterText text={`GM, ${crypticPhrase}`} data-id="element-375" />
              </p>
            </div>
          </div>
        </motion.div>
      </nav>

      <main className="relative z-10" data-id="element-376">
        <Hero data-id="element-377" />

        {/* My Work Section */}
        <motion.section id="work" style={{
        y: yProjects
      }} className="py-16 md:py-24 px-4 md:px-12 w-full relative" data-id="element-378">
          <div className="max-w-[1800px] mx-auto" data-id="element-379">
            <div className="mb-8 md:mb-16 border-b pb-6 md:pb-8" style={{
            borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'
          }} data-id="element-380">
              <h2 className={`text-4xl md:text-6xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-black'}`} data-id="element-381">
                My Work
              </h2>
            </div>

            <div className="flex flex-col lg:flex-row gap-12" data-id="element-382">
              <div className="hidden lg:block" data-id="element-383">
                <Sidebar categories={allCategories} activeCategory={activeCategory} onSelectCategory={handleCategoryChange} data-id="element-384" />
              </div>

              <div className="lg:hidden" data-id="element-385">
                <MobileFilterDropdown categories={allCategories} activeCategory={activeCategory} onSelectCategory={handleCategoryChange} data-id="element-386" />
              </div>

              <div className="flex-1 lg:w-[80%]" data-id="element-387">
                {isLoadingProjects ? <WorksLoading data-id="element-388" /> : visibleProjects.length === 0 ? <div className="text-center py-16" data-id="element-389">
                    <p className={`text-xl ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`} data-id="element-390">
                      No projects found in this category.
                    </p>
                  </div> : <>
                    <MasonryGrid key={activeCategory} projects={visibleProjects} onProjectClick={onProjectClick} selectedCategory={activeCategory} data-id="element-391" />

                    {hasMore && <div className="mt-16 flex justify-center" data-id="element-392">
                        <button onClick={handleLoadMore} className={`group relative px-8 py-4 font-bold rounded-full overflow-hidden transition-opacity duration-300 hover:opacity-70 ${isDark ? 'bg-white text-black' : 'bg-black text-white'}`} data-id="element-393">
                          <span className="relative z-10" data-id="element-394">Load More</span>
                        </button>
                      </div>}
                  </>}
              </div>
            </div>
          </div>
        </motion.section>

        {/* About Me Section */}
        <motion.section id="about" style={{
        y: yAbout
      }} className="py-16 md:py-32 px-4 md:px-12 relative" data-id="element-395">
          <div className="max-w-4xl mx-auto" data-id="element-396">
            <motion.span initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} className="text-sm mb-8 block uppercase tracking-widest font-light border-t pt-8" style={{
            color: isDark ? '#999' : '#666',
            borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'
          }} data-id="element-397">
              About me
            </motion.span>
            <p className={`text-3xl md:text-5xl font-bold ${isDark ? 'text-white' : 'text-black'}`} style={{
            lineHeight: '1.15'
          }} data-id="element-398">
              Hi! I'm Javier. For over 20 years, I've been creating{' '}
              <span style={{
              color: isDark ? 'rgb(115, 115, 115)' : '#666'
            }} data-id="element-399">
                digital products
              </span>{' '}
              that explore the dialogue between{' '}
              <span style={{
              color: isDark ? 'rgb(115, 115, 115)' : '#666'
            }} data-id="element-400">
                human creativity
              </span>{' '}
              and an increasingly digital world, crafting immersive and visually
              striking experiences.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mt-24" data-id="element-401">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0
            }} className="border-t pt-6" style={{
              borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'
            }} data-id="element-402">
                {/* Artist Icon - Capsule Pill */}
                <div className="mb-6 w-10 h-10" data-id="element-403">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" style={{
                  color: isDark ? '#fff' : '#000'
                }} data-id="element-404">
                    {/* Outer square frame with 2px radius */}
                    <motion.rect x="5" y="5" width="30" height="30" stroke="currentColor" strokeWidth="1.5" rx="2" initial={{
                    pathLength: 0,
                    opacity: 0
                  }} whileInView={{
                    pathLength: 1,
                    opacity: 1
                  }} transition={{
                    duration: 1,
                    delay: 0.2,
                    ease: 'easeOut'
                  }} viewport={{
                    once: true
                  }} data-id="element-405" />

                    {/* Sleek Capsule/Pill shape - wider and thinner */}
                    <motion.rect x="12" y="17.5" width="16" height="5" rx="2.5" stroke="currentColor" strokeWidth="1.5" fill="none" initial={{
                    pathLength: 0,
                    opacity: 0
                  }} whileInView={{
                    pathLength: 1,
                    opacity: 1
                  }} transition={{
                    duration: 0.8,
                    delay: 1.2,
                    ease: 'easeOut'
                  }} viewport={{
                    once: true
                  }} data-id="element-406" />

                    {/* Vertical line in the middle */}
                    <motion.line x1="20" y1="17.5" x2="20" y2="22.5" stroke="currentColor" strokeWidth="1.5" initial={{
                    pathLength: 0,
                    opacity: 0
                  }} whileInView={{
                    pathLength: 1,
                    opacity: 1
                  }} transition={{
                    duration: 0.4,
                    delay: 2.0,
                    ease: 'easeOut'
                  }} viewport={{
                    once: true
                  }} data-id="element-407" />
                  </svg>
                </div>
                <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-black'}`} data-id="element-408">
                  Artist
                </h3>
                <p style={{
                color: isDark ? 'rgb(163, 163, 163)' : '#222'
              }} data-id="element-409">
                  I work with atmosphere and nostalgia, blending Japanese
                  aesthetics, typography work and retro-future elements.
                </p>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className="border-t pt-6" style={{
              borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'
            }} data-id="element-410">
                {/* Builder Icon - Square Terminal */}
                <div className="mb-6 w-10 h-10" data-id="element-411">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" style={{
                  color: isDark ? '#fff' : '#000'
                }} data-id="element-412">
                    {/* Square frame with 2px radius */}
                    <motion.rect x="5" y="5" width="30" height="30" rx="2" stroke="currentColor" strokeWidth="1.5" initial={{
                    pathLength: 0,
                    opacity: 0
                  }} whileInView={{
                    pathLength: 1,
                    opacity: 1
                  }} transition={{
                    duration: 1,
                    delay: 0.2,
                    ease: 'easeOut'
                  }} viewport={{
                    once: true
                  }} data-id="element-413" />
                    {/* Top bar */}
                    <motion.line x1="5" y1="13" x2="35" y2="13" stroke="currentColor" strokeWidth="1.5" initial={{
                    pathLength: 0,
                    opacity: 0
                  }} whileInView={{
                    pathLength: 1,
                    opacity: 1
                  }} transition={{
                    duration: 0.5,
                    delay: 1.2,
                    ease: 'easeOut'
                  }} viewport={{
                    once: true
                  }} data-id="element-414" />
                    {/* Chevron > */}
                    <motion.path d="M12.5 20L16 23.5L12.5 27" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" initial={{
                    pathLength: 0,
                    opacity: 0
                  }} whileInView={{
                    pathLength: 1,
                    opacity: 1
                  }} transition={{
                    duration: 0.4,
                    delay: 1.7,
                    ease: 'easeOut'
                  }} viewport={{
                    once: true
                  }} data-id="element-415" />
                    {/* Cursor line */}
                    <motion.line x1="20" y1="27" x2="27.5" y2="27" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" initial={{
                    pathLength: 0,
                    opacity: 0
                  }} whileInView={{
                    pathLength: 1,
                    opacity: 1
                  }} transition={{
                    duration: 0.4,
                    delay: 2.1,
                    ease: 'easeOut'
                  }} viewport={{
                    once: true
                  }} data-id="element-416" />
                  </svg>
                </div>
                <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-black'}`} data-id="element-417">
                  Builder
                </h3>
                <p style={{
                color: isDark ? 'rgb(163, 163, 163)' : '#222'
              }} data-id="element-418">
                  Functionality meets aesthetics. Clean digital experiences that
                  are robust, efficient, and visually flawless.
                </p>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4
            }} className="border-t pt-6" style={{
              borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'
            }} data-id="element-419">
                {/* Crypto Icon - Lightning Bolt in Square */}
                <div className="mb-6 w-10 h-10" data-id="element-420">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" style={{
                  color: isDark ? '#fff' : '#000'
                }} data-id="element-421">
                    {/* Outer square frame with 2px radius */}
                    <motion.rect x="5" y="5" width="30" height="30" rx="2" stroke="currentColor" strokeWidth="1.5" initial={{
                    pathLength: 0,
                    opacity: 0
                  }} whileInView={{
                    pathLength: 1,
                    opacity: 1
                  }} transition={{
                    duration: 1,
                    delay: 0.2,
                    ease: 'easeOut'
                  }} viewport={{
                    once: true
                  }} data-id="element-422" />
                    {/* Lightning bolt */}
                    <motion.path d="M22.5 11L14 21H20L17.5 29L26 19H20L22.5 11Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" initial={{
                    pathLength: 0,
                    opacity: 0
                  }} whileInView={{
                    pathLength: 1,
                    opacity: 1
                  }} transition={{
                    duration: 0.8,
                    delay: 1.2,
                    ease: 'easeOut'
                  }} viewport={{
                    once: true
                  }} data-id="element-423" />
                  </svg>
                </div>
                <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-black'}`} data-id="element-424">
                  Crypto Native
                </h3>
                <p style={{
                color: isDark ? 'rgb(163, 163, 163)' : '#222'
              }} data-id="element-425">
                  Dedicated web3 enthusiast and active member of the crypto
                  community since 2019 in many roles as Artist, Founder and
                  Builder.
                </p>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Companies Section */}
        <section className="py-12 md:py-24 px-4 md:px-12 relative overflow-hidden" data-id="element-426">
          <div className="max-w-6xl mx-auto" data-id="element-427">
            <motion.span initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} className="text-sm mb-12 block uppercase tracking-widest font-light text-center" style={{
            color: isDark ? '#999' : '#666'
          }} data-id="element-428">
              Trusted by
            </motion.span>

            {/* Infinite Ticker Tape */}
            <div className="relative" data-id="element-429">
              {/* Gradient overlays on edges */}
              <div className="absolute left-0 top-0 bottom-0 w-32 z-10 pointer-events-none" style={{
              background: isDark ? 'linear-gradient(to right, rgb(0, 0, 0) 0%, rgba(0, 0, 0, 0) 100%)' : 'linear-gradient(to right, rgb(255, 255, 255) 0%, rgba(255, 255, 255, 0) 100%)'
            }} data-id="element-430" />
              <div className="absolute right-0 top-0 bottom-0 w-32 z-10 pointer-events-none" style={{
              background: isDark ? 'linear-gradient(to left, rgb(0, 0, 0) 0%, rgba(0, 0, 0, 0) 100%)' : 'linear-gradient(to left, rgb(255, 255, 255) 0%, rgba(255, 255, 255, 0) 100%)'
            }} data-id="element-431" />

              {/* Scrolling container */}
              <div className="flex overflow-hidden" data-id="element-432">
                <motion.div className="flex gap-16 pr-16" animate={{
                x: [0, -100 * companies.length]
              }} transition={{
                x: {
                  repeat: Infinity,
                  repeatType: 'loop',
                  duration: 30,
                  ease: 'linear'
                }
              }} data-id="element-433">
                  {/* First set of logos */}
                  {companies.map((company, i) => <div key={`first-${company.name}-${i}`} className="flex items-center justify-center flex-shrink-0" style={{
                  width: '150px'
                }} data-id="element-434">
                      <img src={company.logo} alt={company.name} className="w-full h-auto max-h-12 object-contain" style={{
                    filter: 'brightness(0) invert(1)',
                    transform: company.name === 'Sky' ? 'scale(0.9)' : undefined
                  }} data-id="element-435" />
                    </div>)}
                  {/* Duplicate set for seamless loop */}
                  {companies.map((company, i) => <div key={`second-${company.name}-${i}`} className="flex items-center justify-center flex-shrink-0" style={{
                  width: '150px'
                }} data-id="element-436">
                      <img src={company.logo} alt={company.name} className="w-full h-auto max-h-12 object-contain" style={{
                    filter: 'brightness(0) invert(1)',
                    transform: company.name === 'Sky' ? 'scale(0.9)' : undefined
                  }} data-id="element-437" />
                    </div>)}
                  {/* Third set for extra smoothness */}
                  {companies.map((company, i) => <div key={`third-${company.name}-${i}`} className="flex items-center justify-center flex-shrink-0" style={{
                  width: '150px'
                }} data-id="element-438">
                      <img src={company.logo} alt={company.name} className="w-full h-auto max-h-12 object-contain" style={{
                    filter: 'brightness(0) invert(1)',
                    transform: company.name === 'Sky' ? 'scale(0.9)' : undefined
                  }} data-id="element-439" />
                    </div>)}
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className={`min-h-[60vh] flex flex-col justify-center px-4 md:px-12 relative z-20 border-t ${isDark ? 'bg-black' : 'bg-white'}`} style={{
        borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
      }} data-id="element-440">
          <div className="max-w-7xl mx-auto w-full" data-id="element-441">
            <ContactSlotMachineText data-id="element-442" />

            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-12" data-id="element-443">
              <div data-id="element-444">
                <button onClick={() => setIsContactModalOpen(true)} className={`text-2xl md:text-4xl border-b-2 pb-2 transition-all duration-300 inline-flex items-center gap-4 hover:opacity-70 ${isDark ? 'border-white/30 hover:border-white text-white' : 'border-black/30 hover:border-black text-black'}`} data-id="element-445">
                  Write a line
                  <Pencil className="w-6 h-6 md:w-8 md:h-8" data-id="element-446" />
                </button>
              </div>

              <div className="flex gap-6" data-id="element-447">
                <a href="https://x.com/javpixel_art" target="_blank" rel="noopener noreferrer" className={`w-12 h-12 border rounded-full flex items-center justify-center transition-opacity duration-300 hover:opacity-70 ${isDark ? 'border-white/20' : 'border-black/20'}`} aria-label="X (Twitter)" data-id="element-448">
                  <XLogo className="w-5 h-5" data-id="element-449" />
                </a>
              </div>
            </div>
          </div>

          <footer className="absolute bottom-8 left-0 w-full px-4 md:px-12 flex justify-between text-xs md:text-sm uppercase tracking-wide font-light border-t pt-8" style={{
          color: isDark ? '#999' : '#666',
          borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
        }} data-id="element-450">
            <span data-id="element-451">© {new Date().getFullYear()} Javstuff.com</span>
            <span data-id="element-452">All Rights Reserved</span>
          </footer>
        </section>
      </main>

      <ContactModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} data-id="element-453" />
    </div>;
}